import React, { useState } from 'react';
import { FaGoogle } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface LoginFormProps {
  onClose?: () => void;
  onSwitchToSignUp?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose, onSwitchToSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle } = useAuth();
  const { mode } = useTheme();
  const { t } = useTranslation();
  const isFairyTheme = mode === 'fairy';
  const isNeonTheme = mode === 'neon';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      return;
    }

    try {
      setLoading(true);
      await signIn(email, password);
      onClose?.();
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      await signInWithGoogle();
      onClose?.();
    } catch (error) {
      console.error('Google sign-in error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className={`text-2xl font-bold mb-2 ${isNeonTheme ? 'text-[#00FF00]' : isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
          {t('auth.welcome_back')}
        </h2>
        <p className="text-gray-400">{t('auth.sign_in')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={`block text-sm font-medium mb-1 ${isNeonTheme ? 'text-[#00FF00]' : isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
            {t('auth.email')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg ${isNeonTheme
              ? 'bg-black border border-[#00FF00]/20 text-[#00FF00] focus:border-[#00FF00]'
              : isFairyTheme
                ? 'bg-purple-900/20 border border-purple-500/20 text-purple-200 focus:border-purple-500'
                : 'bg-gray-700 border border-gray-600 text-white focus:border-primary'
              }`}
            required
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${isNeonTheme ? 'text-[#00FF00]' : isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
            {t('auth.password')}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg ${isNeonTheme
              ? 'bg-black border border-[#00FF00]/20 text-[#00FF00] focus:border-[#00FF00]'
              : isFairyTheme
                ? 'bg-purple-900/20 border border-purple-500/20 text-purple-200 focus:border-purple-500'
                : 'bg-gray-700 border border-gray-600 text-white focus:border-primary'
              }`}
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2 rounded-lg font-medium transition-colors ${isNeonTheme
            ? 'bg-[#00FF00] text-black hover:bg-[#00FF00]/90'
            : isFairyTheme
              ? 'bg-purple-500 text-white hover:bg-purple-600'
              : 'bg-primary text-white hover:bg-primary/90'
            }`}
        >
          {loading ? t('common.loading') : t('auth.sign_in')}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-600"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className={`px-2 ${isNeonTheme ? 'bg-black text-[#00FF00]' : isFairyTheme ? 'bg-[#1a1625] text-purple-200' : 'bg-gray-800 text-gray-400'}`}>
            {t('auth.or_continue_with')}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={loading}
        className={`w-full py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${isNeonTheme
          ? 'bg-black border border-[#00FF00]/20 text-[#00FF00] hover:bg-[#00FF00]/10'
          : isFairyTheme
            ? 'bg-purple-900/20 border border-purple-500/20 text-purple-200 hover:bg-purple-500/20'
            : 'bg-gray-700 border border-gray-600 text-white hover:bg-gray-600'
          }`}
      >
        <FaGoogle />
        <span>{t('auth.google')}</span>
      </button>

      <div className="text-center">
        <p className="text-sm text-gray-400">
          {t('auth.no_account')}{' '}
          <button
            onClick={onSwitchToSignUp}
            className={`font-medium ${isNeonTheme
              ? 'text-[#00FF00] hover:text-[#00FF00]/80'
              : isFairyTheme
                ? 'text-purple-400 hover:text-purple-300'
                : 'text-primary hover:text-primary/80'
              }`}
          >
            {t('auth.sign_up')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm; 